import { useMemo, useState, useEffect } from "react";
import { checkApi } from "../../Authentication/AxiosInstance";

export const PredictMatches = ({ 
  data, 
  displayData, 
  searchObject, 
  baseColumns,
  onDisplayDataChange,
  onColumnsChange 
}) => {
  const [personalRank, setPersonalRank] = useState('');
  const [showMyColleges, setShowMyColleges] = useState(false);
  const [matchedColleges, setMatchedColleges] = useState([]);
  const [isLoadingMatches, setIsLoadingMatches] = useState(false);
  const [matchError, setMatchError] = useState(null);

  // Transform data for prediction algorithm
  const transformDataForPrediction = (cutoffData) => {
    return cutoffData.map(item => {
      // Extract year from academic year (e.g., "AY202526" -> 2025)
      const academicYear = item.c001_academicyear || '';
      let year = null;
      if (academicYear.startsWith('AY') && academicYear.length >= 6) {
        year = parseInt(academicYear.substring(2, 6));
      }
      
      // Extract round number (e.g., "Round 1" -> 1)
      let round = null;
      if (item.c001_round) {
        const roundMatch = item.c001_round.toString().match(/\d+/);
        round = roundMatch ? parseInt(roundMatch[0]) : null;
      }
      
      // Convert closing rank to int
      let closingRank = null;
      if (item.c001_clrank) {
        try {
          closingRank = parseInt(item.c001_clrank);
        } catch (e) {
          closingRank = null;
        }
      }
      
      return {
        college: item.c001_instname || '',
        branch: item.c001_branchname || '',
        category: item.c001_caste || '',
        board: item.c001_allocationboard || '',
        year: year,
        round: round,
        closing_rank: closingRank
      };
    }).filter(item => 
      item.college && 
      item.branch && 
      item.category && 
      item.board && 
      item.year && 
      item.round !== null && 
      item.closing_rank !== null
    );
  };

  // Function to fetch matched colleges
  const fetchMatchedColleges = async () => {
    if (!personalRank || !data || data.length === 0) {
      setMatchError("Please enter your rank and ensure data is loaded");
      return;
    }

    // Extract category and board from searchObject
    const userCategory = searchObject.category || '';
    const userBoard = searchObject.board || '';
    
    if (!userCategory || !userBoard) {
      setMatchError("Please select category and board in filters");
      return;
    }

    setIsLoadingMatches(true);
    setMatchError(null);

    try {
      // Transform data for prediction
      const transformedData = transformDataForPrediction(data);
      
      if (transformedData.length === 0) {
        setMatchError("No valid cutoff data available for prediction");
        setIsLoadingMatches(false);
        return;
      }

      // Call the prediction API
      const response = await checkApi.post('/capzone/predict-colleges/', {
        user_rank: parseInt(personalRank),
        user_category: userCategory,
        user_board: userBoard,
        cutoff_data: transformedData
      });

      const matches = response.data.matches || [];
      console.log('Matched colleges from API:', matches);
      console.log('Sample match:', matches[0]);
      setMatchedColleges(matches);
    } catch (error) {
      console.error('Error fetching matched colleges:', error);
      setMatchError(
        error?.response?.data?.error || 
        error?.message || 
        "An error occurred while fetching matched colleges"
      );
      setMatchedColleges([]);
    } finally {
      setIsLoadingMatches(false);
    }
  };

  // Handle show matches button click
  const handleShowMatches = () => {
    if (!personalRank) {
      setMatchError("Please enter your rank");
      return;
    }
    fetchMatchedColleges();
    setShowMyColleges(true);
  };

  // Handle show all colleges button click
  const handleShowAllColleges = () => {
    setShowMyColleges(false);
    setMatchedColleges([]);
    setMatchError(null);
  };

  // Enhanced columns with probability, category, and eligibility when showing matches
  const columns = useMemo(() => {
    if (!showMyColleges || matchedColleges.length === 0) {
      return baseColumns;
    }
    
    // Add probability category, probability, and eligibility columns
    return [
      ...baseColumns,
      {
        accessorKey: "probability_category",
        header: "Category",
        footer: "Category",
        enableColumnFilter: false,
        cell: ({ getValue }) => {
          const category = getValue();
          if (!category) return <span>-</span>;
          let colorClass = "text-gray-700 font-bold";
          if (category === "High") colorClass = "p-2 m-2 bg-green-700 font-bold text-white rounded-xl";
          else if (category === "Medium") colorClass = "p-2 m-2 bg-orange-700 font-bold text-white rounded-xl ";
          else colorClass = "p-2 m-2 bg-red-700 font-bold text-white rounded-xl";
          return <span className={colorClass}>{category}</span>;
        }
      },
      {
        accessorKey: "probability",
        header: "Admission Probability (%)",
        footer: "Admission Probability (%)",
        enableColumnFilter: false,
        cell: ({ getValue }) => {
          const prob = getValue();
          if (prob === undefined || prob === null) return <span>-</span>;
          let colorClass = "text-gray-700";
          if (prob >= 85) colorClass = "text-green-700 font-bold";
          else if (prob >= 50) colorClass = "text-yellow-700";
          else colorClass = "text-orange-700";
          return <span className={colorClass}>{prob}%</span>;
        }
      },
      {
        accessorKey: "is_eligible",
        header: "Eligible",
        footer: "Eligible",
        enableColumnFilter: false,
        cell: ({ getValue }) => {
          const eligible = getValue();
          if (eligible === undefined || eligible === null) return <span>-</span>;
          return (
            <span className={eligible ? "text-green-700 font-bold" : "text-red-700"}>
              {eligible ? "✓ Yes" : "✗ No"}
            </span>
          );
        }
      }
    ];
  }, [baseColumns, showMyColleges, matchedColleges]);

  // Update parent component when columns change
  useEffect(() => {
    onColumnsChange?.(columns);
  }, [columns, onColumnsChange]);

  // Merge matched colleges data with displayData when showMyColleges is true
  // Backend returns 5 High + 3 Medium + 2 Low probability colleges
  const finalDisplayData = useMemo(() => {
    if (!showMyColleges || matchedColleges.length === 0) {
      return displayData;
    }
    
    console.log('All matched colleges from backend:', matchedColleges);
    
    // Backend already returns 5 High + 3 Medium + 2 Low, so use all of them
    // Create a map of matched colleges for quick lookup
    const matchedMap = new Map();
    matchedColleges.forEach(match => {
      const key = `${match.college}|${match.branch}`;
      matchedMap.set(key, match);
    });
    
    // Find matching rows in displayData and merge with matched college data
    const mergedData = displayData
      .map(item => {
        const key = `${item.c001_instname}|${item.c001_branchname}`;
        const match = matchedMap.get(key);
        
        if (match) {
          // Merge the matched college data with the original item
          return {
            ...item,
            probability: match.probability,
            is_eligible: match.is_eligible,
            expected_rank: match.expected_rank,
            threshold: match.threshold,
            probability_category: match.probability_category || 
              (match.probability >= 85 ? "High" : 
               match.probability >= 50 ? "Medium" : "Low")
          };
        }
        return null;
      })
      .filter(item => item !== null); // Only keep matched colleges
    
    // Sort by probability category (High, Medium, Low) then by expected_rank
    const categoryOrder = { "High": 1, "Medium": 2, "Low": 3 };
    mergedData.sort((a, b) => {
      const catA = categoryOrder[a.probability_category] || 4;
      const catB = categoryOrder[b.probability_category] || 4;
      if (catA !== catB) {
        return catA - catB;
      }
      const rankA = a.expected_rank || Infinity;
      const rankB = b.expected_rank || Infinity;
      return rankA - rankB;
    });
    
    console.log('Final display data:', mergedData);
    
    return mergedData;
  }, [displayData, showMyColleges, matchedColleges]);

  // Update parent component when display data changes
  useEffect(() => {
    onDisplayDataChange?.(finalDisplayData);
  }, [finalDisplayData, onDisplayDataChange]);

  return (
    <div className="flex flex-col items-start space-y-2 mb-4">
      <div className="flex flex-row items-center space-x-2">
        <label className="m-1 p-2 font-bold text-cave-900 text-base">
          Enter your rank:
        </label>
        <input 
          type="number" 
          className="m-2 text-center w-32 px-2 py-1
                     text-primary-900 border-2 border-primary-500 rounded-sm" 
          value={personalRank}
          onChange={(e) => setPersonalRank(e.target.value)}
          placeholder="e.g., 5000"
        />
        <button 
          className="buttonClass"
          onClick={handleShowMatches}
          disabled={isLoadingMatches}
        >
          {isLoadingMatches ? "Loading..." : "Show My Matches"}
        </button>
        <button 
          className="buttonClass"
          onClick={handleShowAllColleges}
          disabled={isLoadingMatches}
        >
          Show All Colleges
        </button>
      </div>
      {matchError && (
        <div className="text-red-600 text-sm font-semibold ml-3">
          {matchError}
        </div>
      )}
      {showMyColleges && finalDisplayData.length > 0 && (
        <div className="text-green-700 text-sm font-semibold ml-3">
          Found {finalDisplayData.length} matching college{finalDisplayData.length !== 1 ? 's' : ''} (5 High + 3 Medium + 2 Low probability)!
        </div>
      )}
      {showMyColleges && matchedColleges.length > 0 && finalDisplayData.length === 0 && (
        <div className="text-yellow-700 text-sm font-semibold ml-3">
          No matching colleges found. Try adjusting your rank or filters.
        </div>
      )}
    </div>
  );
};

